/**
 * App-state persistence backed by IndexedDB.
 *
 * Replaces the previous single-key localStorage approach: IndexedDB is async,
 * has a far larger quota, and stores structured data without blocking the main
 * thread. The persisted payload stays human-readable JSON (the `content`
 * string), so nothing about the data format changes — only where it lives.
 *
 * Theme preference deliberately stays in localStorage (see localStorage.ts):
 * the FOUC-avoidance script in public/index.html reads it synchronously before
 * the bundle loads, which IndexedDB cannot serve.
 */

import { logger } from './logger';

export interface PersistedAppState {
  content: string;
  filename: string;
  showHelp: boolean;
  lastSaved: string;
  /** Optional so records written before the Today panel existed stay valid. */
  todayPanelCollapsed?: boolean;
}

const DB_NAME = 'better-tdl';
const DB_VERSION = 1;
const STORE_NAME = 'app-state';
const STATE_KEY = 'current';

// Key of the pre-IndexedDB localStorage record, migrated once on first run.
const LEGACY_LS_KEY = 'better-tdl-app-state';

let dbPromise: Promise<IDBDatabase> | null = null;

/** Whether IndexedDB is usable in this environment (false in some private modes). */
export const isPersistenceAvailable = (): boolean => {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
};

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  // Don't cache a rejected promise — allow a later retry.
  dbPromise.catch(() => {
    dbPromise = null;
  });

  return dbPromise;
}

/** Persist the current app state, stamping it with the save time. */
export const saveAppState = async (
  state: Omit<PersistedAppState, 'lastSaved'>
): Promise<boolean> => {
  if (!isPersistenceAvailable()) return false;

  try {
    const db = await openDB();
    const record: PersistedAppState = { ...state, lastSaved: new Date().toISOString() };

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).put(record, STATE_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });

    return true;
  } catch (error) {
    logger.error('Failed to save app state to IndexedDB', error);
    return false;
  }
};

/** Load the persisted app state, or null if none/invalid. */
export const loadAppState = async (): Promise<PersistedAppState | null> => {
  if (!isPersistenceAvailable()) return null;

  try {
    const db = await openDB();
    const result = await new Promise<any>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const request = tx.objectStore(STORE_NAME).get(STATE_KEY);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (
      result &&
      typeof result.content === 'string' &&
      typeof result.filename === 'string' &&
      typeof result.showHelp === 'boolean' &&
      typeof result.lastSaved === 'string'
    ) {
      return result as PersistedAppState;
    }

    if (result) {
      logger.warn('Invalid app state structure in IndexedDB, ignoring');
    }
    return null;
  } catch (error) {
    logger.error('Failed to load app state from IndexedDB', error);
    return null;
  }
};

/** Remove the persisted app state. */
export const clearAppState = async (): Promise<boolean> => {
  if (!isPersistenceAvailable()) return false;

  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(STATE_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
    return true;
  } catch (error) {
    logger.error('Failed to clear app state from IndexedDB', error);
    return false;
  }
};

/**
 * One-time migration of the pre-IndexedDB localStorage record into IndexedDB.
 * Runs on startup; no-op once migrated (or if there was nothing to migrate).
 */
export const migrateLegacyLocalStorage = async (): Promise<void> => {
  if (!isPersistenceAvailable()) return;

  try {
    if (typeof localStorage === 'undefined') return;
    const raw = localStorage.getItem(LEGACY_LS_KEY);
    if (!raw) return;

    // Only import if IndexedDB has nothing yet; otherwise IndexedDB wins.
    const existing = await loadAppState();
    if (!existing) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.content === 'string' && typeof parsed.filename === 'string') {
        await saveAppState({
          content: parsed.content,
          filename: parsed.filename,
          showHelp: typeof parsed.showHelp === 'boolean' ? parsed.showHelp : false,
        });
        logger.info('Migrated app state from localStorage to IndexedDB');
      }
    }

    // Remove the legacy key either way so this runs at most once.
    localStorage.removeItem(LEGACY_LS_KEY);
  } catch (error) {
    logger.error('Failed to migrate legacy localStorage app state', error);
  }
};

// --- Debounced autosave ------------------------------------------------------

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let pendingState: Omit<PersistedAppState, 'lastSaved'> | null = null;
let pendingOnSave: (() => void) | undefined;

/**
 * Debounced save to avoid an IndexedDB write on every keystroke/edit. The most
 * recent state wins; `onSave` fires after the write actually lands.
 */
export const debouncedSaveAppState = (
  state: Omit<PersistedAppState, 'lastSaved'>,
  delay: number = 600,
  onSave?: () => void
): void => {
  pendingState = state;
  pendingOnSave = onSave;

  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    void flushAppState();
  }, delay);
};

/**
 * Immediately write any pending debounced state (e.g. on page hide / unmount)
 * so the last edit is never lost.
 */
export const flushAppState = async (): Promise<void> => {
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  const state = pendingState;
  const onSave = pendingOnSave;
  pendingState = null;
  pendingOnSave = undefined;

  if (state) {
    const ok = await saveAppState(state);
    if (ok) onSave?.();
  }
};

/**
 * Best-effort storage usage/quota for the origin via the Storage API.
 * Returns null when unavailable (older browsers, jsdom).
 */
export const getStorageEstimate = async (): Promise<{ usage: number; quota: number } | null> => {
  try {
    if (typeof navigator !== 'undefined' && navigator.storage && navigator.storage.estimate) {
      const estimate = await navigator.storage.estimate();
      return { usage: estimate.usage ?? 0, quota: estimate.quota ?? 0 };
    }
  } catch (error) {
    logger.error('Failed to read storage estimate', error);
  }
  return null;
};
